import { ChangeDetectionStrategy, Component, DestroyRef, Inject, LOCALE_ID, OnInit, Signal, computed, effect } from '@angular/core';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { validPhoneNumber } from '@platform/shared/utilities/phonenumber-validator';
import { OrganizationUnitDisallowedCharacters, OrganizationUnitValidationPattern } from '@platform/shared/utilities/validation-utility';
import { selectUser } from '@platform/platform.selectors';
import { Observable, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { TranslateService } from '@ngx-translate/core';
import { Error, ResourceState, ResourceStatuses, filterSucceededResource } from '@platform/shared/resource.state';
import { CurrentUser } from '@platform/platform.state';
import { parsePhoneNumberWithError as parsePhoneNumber } from 'libphonenumber-js';
import { toDateOnly } from '@platform/shared/date-only';
import { PutUserProfileDto } from '@core/users/user.model';
import * as CoreActions from '@core/core.actions';
import * as RouterActions from '@platform/router.actions';
import moment from 'moment';
import { selectUserOrganizationUnits, selectUserProfile } from '@core/core.selectors';
import { User, UserProfile } from '@core/core.state';
import { formatDate } from '@angular/common';
import {
  OptionItem,
  emailValidator,
  maxLength,
  pattern,
  required,
  setControlDisabled,
  setControlEnabled,
} from '@platform/components/forms/forms.utils';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserProfileComponent implements OnInit {
  form: UntypedFormGroup = this.fb.group({
    firstName: ['', [required, maxLength(200)]],
    lastName: ['', [required, maxLength(200)]],
    organizationUnit: ['', [required, maxLength(200), pattern(OrganizationUnitValidationPattern)]],
    mobileNumber: ['', [validPhoneNumber(), required]],
    mail: ['', [required, maxLength(200), emailValidator()]],
    language: ['German', [required]],
    active: ['Active', [required]],
    externalId: [''],
    areMailNotificationsOn: [true],
    notificationInterval: ['Weeks1', [required]],
    activeUntil: this.fb.control<Date | null>(null),
  });

  filteredOrganizationUnits$: Observable<string[]>;
  showExternalId = true;
  lastSuccessfulLogin: string;
  createdAt: string;
  disableProfilePictureChange = false;

  isInternalUser: Signal<boolean>;

  intervalOptionsTranslated: OptionItem[] = [];

  savePending: Signal<boolean>;
  currentUserId: Signal<string>;
  userDeputies: User[] = [];
  changePasswordUrl: string;
  thumbnailPhoto: string;

  private userProfileResource: Signal<ResourceState<UserProfile> | undefined>;
  private currentUser: Signal<CurrentUser | undefined>;
  private userProfile: Signal<UserProfile | undefined>;
  private organizationUnits: string[] = [];
  private startDate = moment();

  constructor(
    private fb: FormBuilder,
    private store$: Store<RootState>,
    private translate: TranslateService,
    @Inject(LOCALE_ID) private locale: string,
    private destroyRef: DestroyRef,
  ) {
    this.currentUser = toSignal(store$.pipe(select(selectUser), filterSucceededResource()));
    this.currentUserId = computed(() => this.currentUser()?.id || '');
    this.isInternalUser = computed(() => Boolean(this.currentUser()?.isInternal));

    this.userProfileResource = toSignal(store$.pipe(select(selectUserProfile)));
    this.savePending = computed(() => this.userProfileResource()?.status === ResourceStatuses.PENDING);
    this.userProfile = computed(() => this.userProfileResource()?.data);

    this.filteredOrganizationUnits$ = this.form.get('organizationUnit')!.valueChanges.pipe(
      startWith(''),
      map((val) => this.filterOrganizationUnits(val)),
    );

    effect(() => {
      if (this.isInternalUser()) {
        ['firstName', 'lastName', 'organizationUnit', 'mobileNumber', 'mail', 'active'].forEach((controlName) =>
          setControlDisabled(this.form.get(controlName)),
        );
      } else {
        ['organizationUnit', 'mail', 'active'].forEach((controlName) => setControlDisabled(this.form.get(controlName)));
      }
    });

    // profile user loaded
    effect(() => {
      if (!this.currentUser()) {
        return;
      }

      if (!this.userProfile()) {
        return;
      }
    });
  }

  get phoneNumberValidationRules() {
    return {
      invalidPhoneNumber: 'users.user-form.error.mobile-number-invalid',
    };
  }

  get organizationUnitValidationRules() {
    return {
      pattern: () => this.getOrgUnitDisallowedCharactersValidationMessage(),
    };
  }

  get mailValidationRules() {
    return {
      backendError: () => this.form.get('mail')!.getError('backendError'),
    };
  }

  ngOnInit(): void {
    this.store$.pipe(select(selectUserProfile), takeUntilDestroyed(this.destroyRef)).subscribe((state) => this.onUserProfileLoaded(state));
    this.form
      .get('areMailNotificationsOn')!
      .valueChanges.pipe(distinctUntilChanged())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        setControlEnabled(this.form.get('notificationInterval'), Boolean(value));
      });

    this.store$
      .pipe(
        select(selectUserOrganizationUnits),
        filter((state) => state.status === ResourceStatuses.SUCCEEDED && !!state.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => (this.organizationUnits = state.data));
  }

  save() {
    let phoneNumber;
    if (this.form.get('mobileNumber')!.value) {
      phoneNumber = parsePhoneNumber(this.form.get('mobileNumber')!.value, 'CH');
    }
    let activeUntilDate = this.form.get('activeUntil')!.value;
    if (activeUntilDate != null) {
      activeUntilDate = toDateOnly(activeUntilDate);
    }

    const user: PutUserProfileDto = {
      firstName: this.form.get('firstName')!.value,
      lastName: this.form.get('lastName')!.value,
      organizationUnit: this.form.get('organizationUnit')!.value,
      mobileNumber: phoneNumber?.number ?? '',
      email: this.form.get('mail')!.value,
      language: this.form.get('language')!.value,
      isActive: this.form.get('active')!.value === 'Active',
      areMailNotificationsOn: this.form.get('areMailNotificationsOn')!.value,
      notificationInterval: this.form.get('notificationInterval')!.value,
      activeUntil: activeUntilDate,
    };

    this.store$.dispatch(CoreActions.updateUserProfile({ userId: this.currentUserId(), userProfile: user }));
  }

  public getOrgUnitDisallowedCharactersValidationMessage(): string {
    return this.translate.instant('users.user-form.error.invalid-format') + OrganizationUnitDisallowedCharacters;
  }

  public navigateBack($event: any) {
    $event.preventDefault();
    $event.stopPropagation();
    this.store$.dispatch(RouterActions.back());
  }

  private filterOrganizationUnits(val: string): string[] {
    if (!val) {
      return this.organizationUnits;
    }

    return this.organizationUnits.filter((organizationUnit) => organizationUnit.toLowerCase().includes(val.toLowerCase()));
  }

  private onUserProfileLoaded(state: ResourceState<UserProfile>) {
    if (state.data) {
      if (state.status === ResourceStatuses.SUCCEEDED) {
        console.log({ onUserProfileLoaded: state.status });
        const profile = this.userProfile()!;

        this.userDeputies = state.data.deputies;
        this.changePasswordUrl = state.data.changePasswordUrl;
        this.thumbnailPhoto = state.data.thumbnailPhoto;

        this.form.get('externalId')!.disable();

        this.lastSuccessfulLogin =
          profile.lastSuccessfulLoginAt !== null
            ? formatDate(profile.lastSuccessfulLoginAt, 'dd.MM.yyyy HH:mm:ss', this.locale)
            : this.translate.instant('users.user-form.no-activity');

        // for external user
        this.createdAt =
          profile.createdAt !== null
            ? formatDate(profile.createdAt, 'dd.MM.yyyy HH:mm:ss', this.locale)
            : this.translate.instant('users.user-form.no-activity');

        const {
          firstName,
          lastName,
          organizationUnit,
          mobileNumber,
          mail,
          language,
          isActive,
          externalId,
          areMailNotificationsOn,
          notificationInterval,
          activeUntil,
        } = profile;

        this.form.setValue({
          firstName,
          lastName,
          organizationUnit,
          mobileNumber,
          mail,
          language,
          active: isActive ? 'Active' : 'Inactive',
          externalId,
          areMailNotificationsOn,
          notificationInterval,
          activeUntil,
        });
      }

      this.intervalOptionsTranslated = ['Weeks1', 'Hours24', 'Hours12', 'Hours4', 'Hours1', 'Minutes15'].map((value) => ({
        value,
        label: this.translate.instant(`users.user-profile.notifications.interval.${value.toLowerCase()}`),
      }));
    }

    if (state.status === ResourceStatuses.FAILED) {
      if (state.error?.errors) {
        this.setFormErrors(state.error?.errors);
      }
    }
  }

  private setFormErrors(errors: Error) {
    if (errors['Mail']) {
      this.form.get('mail')!.setErrors({
        backendError: errors['Mail'][0],
      });
    }
  }
}
